<script setup lang="ts">
import { ref } from 'vue'
import { Loader2, AlertCircle } from 'lucide-vue-next'
import { Button } from '@/components/ui/button'
import { API, HOST } from '@/utils/http'
import { isSandboxEnvironment } from '@/config/bridge'

const props = defineProps<{ customerId: number }>()
const emit = defineEmits<{ (e: 'success'): void; (e: 'cancel'): void }>()

const loading = ref(false)
const error = ref<string | null>(null)

const handleSubmit = async () => {
  error.value = null
  loading.value = true
  try {
    await API.post(`${HOST}/payments/bridge/customers/${props.customerId}/virtual-accounts`)
    emit('success')
  } catch (err: any) {
    error.value = err.response?.data?.detail || 'Failed to create virtual account. Please try again.'
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <div class="bg-card border rounded p-4 space-y-3 text-sm">
    <p class="text-muted-foreground text-xs leading-relaxed">
      Creates a dedicated US bank account (routing + account number). ACH deposits are auto-converted to USDB on Base.
    </p>

    <div v-if="isSandboxEnvironment()" class="flex items-start gap-2 text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded p-2">
      <AlertCircle class="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
      <span>Virtual accounts are production-only and cannot be created in sandbox.</span>
    </div>

    <div v-else-if="error" class="flex items-start gap-2 text-xs text-destructive bg-destructive/10 border border-destructive/30 rounded p-2">
      <AlertCircle class="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
      <span>{{ error }}</span>
    </div>

    <div class="flex justify-end gap-2">
      <Button variant="outline" size="sm" @click="emit('cancel')" :disabled="loading">Cancel</Button>
      <Button size="sm" @click="handleSubmit" :disabled="loading || isSandboxEnvironment()">
        <Loader2 v-if="loading" class="h-3.5 w-3.5 mr-1.5 animate-spin" />
        Create Virtual Account
      </Button>
    </div>
  </div>
</template>
